import { useEffect, useState } from 'react';
import {
    Stack, Select, TextInput, Textarea, NumberInput, Group, Button,
    Paper, Text, Alert, Loader,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconCamera } from '@tabler/icons-react';
import { authedFetch, uploadFile } from './session';

const URGENCY = [
    { value: 'low',      label: 'Laag — wanneer het uitkomt' },
    { value: 'normal',   label: 'Normaal' },
    { value: 'high',     label: 'Hoog — voertuig deels onbruikbaar' },
    { value: 'critical', label: 'Kritiek — voertuig staat stil' },
];

export default function ServiceRequestForm({ onClose, onSubmitted, prefilledAmbulanceId, prefilledTitle }) {
    const [ambulances, setAmbulances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [created, setCreated] = useState(null);
    const [files, setFiles] = useState([]);

    const [form, setForm] = useState({
        ambulance_id: prefilledAmbulanceId || '',
        title: prefilledTitle || '',
        description: '',
        urgency: 'normal',
        mileage_km: '',
        contact_phone: '',
        preferred_date: '',
        service_provider: 'rescue_technics',
        partner_name: '',
        partner_contact: '',
    });

    useEffect(() => {
        (async () => {
            try {
                const r = await authedFetch('/support/my/ambulances');
                setAmbulances(r.items || []);
                if (!form.ambulance_id && r.items?.length === 1) {
                    setForm((f) => ({ ...f, ambulance_id: String(r.items[0].id) }));
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function setField(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function submit() {
        if (!form.ambulance_id || !form.title || !form.description) {
            setError('Voertuig, titel en omschrijving zijn verplicht.');
            return;
        }
        if (form.service_provider === 'own_partner' && !form.partner_name.trim()) {
            setError('Vul de naam van uw servicepartner in.');
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const r = await authedFetch('/support/my/service-request', {
                method: 'POST',
                body: { ...form, ambulance_id: Number(form.ambulance_id) },
            });
            // Upload images sequentially.
            for (const file of files) {
                try {
                    await uploadFile(`/support/my/service-request/${r.id}/image`, file);
                } catch (err) {
                    console.warn('Image upload failed:', err);
                }
            }
            setCreated(r);
            if (onSubmitted) onSubmitted(r);
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    }

    if (loading) {
        return <Group justify="center" p="xl"><Loader size="sm" /></Group>;
    }

    if (created) {
        return (
            <Stack>
                <Alert color="green" icon={<IconCheck size={20} />} title="Aanvraag ingediend">
                    Uw service-aanvraag #{created.id} is binnen. Onze service-afdeling neemt zo snel mogelijk contact op.
                </Alert>
                {onClose && (
                    <Group justify="flex-end">
                        <Button variant="default" onClick={onClose}>Sluiten</Button>
                    </Group>
                )}
            </Stack>
        );
    }

    if (ambulances.length === 0) {
        return (
            <Alert color="yellow" icon={<IconAlertCircle size={20} />} title="Geen voertuigen gekoppeld">
                Er staan momenteel geen voertuigen op uw account. Neem contact op met onze service-afdeling.
            </Alert>
        );
    }

    return (
        <Stack>
            <Select
                label="Voertuig"
                placeholder="Kies uw ambulance"
                required
                value={form.ambulance_id}
                onChange={(v) => setField('ambulance_id', v)}
                data={ambulances.map((a) => ({
                    value: String(a.id),
                    label: [
                        a.license_plate || a.known_as || `Voertuig #${a.id}`,
                        a.project_number ? `(${a.project_number})` : '',
                        `${a.base_vehicle_make || ''} ${a.base_vehicle_model || ''}`.trim(),
                    ].filter(Boolean).join(' — '),
                }))}
                searchable
            />
            <TextInput
                label="Onderwerp"
                placeholder="Bv. Airco werkt niet meer"
                required
                value={form.title}
                onChange={(e) => setField('title', e.currentTarget.value)}
            />
            <Textarea
                label="Omschrijving"
                placeholder="Wat is het probleem? Wanneer doet het zich voor?"
                minRows={4}
                required
                value={form.description}
                onChange={(e) => setField('description', e.currentTarget.value)}
            />
            <Select
                label="Urgentie"
                data={URGENCY}
                value={form.urgency}
                onChange={(v) => setField('urgency', v || 'normal')}
            />
            <Group grow>
                <NumberInput
                    label="Kilometerstand (optioneel)"
                    min={0}
                    value={form.mileage_km}
                    onChange={(v) => setField('mileage_km', v)}
                />
                <TextInput
                    label="Telefoon (terugbel)"
                    placeholder="06-..."
                    value={form.contact_phone}
                    onChange={(e) => setField('contact_phone', e.currentTarget.value)}
                />
            </Group>
            <TextInput
                label="Voorkeursdatum (optioneel)"
                placeholder="bv. komende week, of 15 mei"
                value={form.preferred_date}
                onChange={(e) => setField('preferred_date', e.currentTarget.value)}
            />

            <Select
                label="Wie voert de service uit?"
                data={[
                    { value: 'rescue_technics', label: 'Rescue Technics' },
                    { value: 'own_partner', label: 'Eigen servicepartner' },
                ]}
                value={form.service_provider}
                onChange={(v) => setField('service_provider', v || 'rescue_technics')}
            />
            {form.service_provider === 'own_partner' && (
                <Group grow>
                    <TextInput
                        label="Naam servicepartner"
                        required
                        placeholder="Bedrijfsnaam"
                        value={form.partner_name}
                        onChange={(e) => setField('partner_name', e.currentTarget.value)}
                    />
                    <TextInput
                        label="Contact partner (optioneel)"
                        placeholder="Telefoon of e-mail"
                        value={form.partner_contact}
                        onChange={(e) => setField('partner_contact', e.currentTarget.value)}
                    />
                </Group>
            )}

            <Paper withBorder p="sm">
                <Group justify="space-between" mb={6}>
                    <Text size="sm" fw={500}>Foto's (optioneel)</Text>
                    <Button
                        size="xs" variant="default" leftSection={<IconCamera size={14} />}
                        onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.capture = 'environment';
                            input.multiple = true;
                            input.onchange = () => {
                                const picked = Array.from(input.files || []);
                                setFiles((prev) => [...prev, ...picked]);
                            };
                            input.click();
                        }}
                    >
                        Foto toevoegen
                    </Button>
                </Group>
                {files.length > 0 ? (
                    <Stack gap={4}>
                        {files.map((f, i) => (
                            <Group key={i} justify="space-between" gap="xs">
                                <Text size="xs">{f.name}</Text>
                                <Button size="compact-xs" variant="subtle" color="red"
                                        onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}>
                                    Verwijder
                                </Button>
                            </Group>
                        ))}
                    </Stack>
                ) : (
                    <Text size="xs" c="dimmed">Geen foto's bijgevoegd. Op mobiel kunt u direct met de camera fotograferen.</Text>
                )}
            </Paper>

            {error && (
                <Alert color="red" icon={<IconAlertCircle size={16} />}>{error}</Alert>
            )}

            <Group justify="flex-end">
                {onClose && <Button variant="default" onClick={onClose} disabled={submitting}>Annuleren</Button>}
                <Button onClick={submit} loading={submitting}>Aanvraag indienen</Button>
            </Group>
        </Stack>
    );
}
